import { ApolloProvider } from '@apollo/client/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter, useLocation } from 'react-router-dom';

import { isProd } from '@repo/common';
import { HydrationMarker } from '@repo/ui/components/HydrationMarker';
import { CLARITY_ID_PROD, CLARITY_ID_TEST } from '@repo/ui/constants/clarity-analytics';
import { GA4_ID_PROD, GA4_ID_TEST } from '@repo/ui/constants/google-analytics';
import {
  sanitizeAnalyticsPagePath,
  useSensitiveRecordingMask,
  useTrackPageView,
} from '@repo/ui/contexts/analytics-context';
import { AnalyticsProvider, ConsentTrackingProviders } from '@repo/ui/contexts/analytics-provider';
import { AUTH_TRANSITION_KIND, publishAuthTransition } from '@repo/ui/contexts/auth-transition';

import { App } from './App';
import { graphQLClient } from './graphql/client';
import i18n from './i18n/i18n';

const oauthParams = new URLSearchParams(window.location.search);
if (oauthParams.has('auth_success')) {
  oauthParams.delete('auth_success');
  const query = oauthParams.toString();
  window.history.replaceState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
  graphQLClient.resetStore().catch(error => console.error('Failed to reset Apollo state after OAuth login', error));
  publishAuthTransition(AUTH_TRANSITION_KIND.SignedIn);
}

interface PartnerClarityCommand {
  (...args: unknown[]): void;
  q?: unknown[][];
}

declare global {
  interface Window {
    clarity?: PartnerClarityCommand;
  }
}

const CLARITY_SCRIPT_ID = 'clarity-script-loader';

function appendScript(id: string, src: string): void {
  if (document.getElementById(id)) return;

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.id = id;
  script.async = true;
  script.defer = true;
  script.src = src;
  document.body.appendChild(script);
}

function initializeClarity(clarityId: string): void {
  if (document.getElementById(CLARITY_SCRIPT_ID)) return;

  if (!window.clarity) {
    const clarityCommand: PartnerClarityCommand = (...args: unknown[]): void => {
      clarityCommand.q = clarityCommand.q || [];
      clarityCommand.q.push(args);
    };
    window.clarity = clarityCommand;
  }

  appendScript(CLARITY_SCRIPT_ID, `https://www.clarity.ms/tag/${clarityId}`);
}

function RouteChangeTracker(): null {
  const location = useLocation();
  const rawPagePath = location.pathname + location.search;
  const pagePath = sanitizeAnalyticsPagePath(rawPagePath);

  useTrackPageView(pagePath);
  useSensitiveRecordingMask(rawPagePath);

  return null;
}

const useProdIds = isProd(window.location.hostname);
const GA4_ID = useProdIds ? GA4_ID_PROD : GA4_ID_TEST;
const CLARITY_ID = useProdIds ? CLARITY_ID_PROD : CLARITY_ID_TEST;

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Failed to find the root element');
}

createRoot(rootElement).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <AnalyticsProvider measurementId={GA4_ID}>
        <ApolloProvider client={graphQLClient}>
          <BrowserRouter>
            <RouteChangeTracker />
            <HydrationMarker />
            <ConsentTrackingProviders clarityId={CLARITY_ID} initializeClarity={initializeClarity} />
            <App />
          </BrowserRouter>
        </ApolloProvider>
      </AnalyticsProvider>
    </I18nextProvider>
  </StrictMode>,
);
